/**
 * S3b — URL-delivered auth sessions (login CSRF / account confusion).
 *
 * supabase-js used to run with detectSessionInUrl + the implicit flow, so a
 * URL carrying ANYONE's session (`#access_token=…&refresh_token=…`, e.g. an
 * attacker's own) on ANY route silently signed the browser into that account
 * and replaced an existing session.
 *
 * Now the client never reads the URL itself (detectSessionInUrl: false). This
 * module captures the auth parameters once, at startup, scrubs them from the
 * address bar/history, and hands them to AuthUrlGate, which decides:
 *   - not an auth callback path      → discarded, no session
 *   - `?code=` (PKCE)                → exchanged; only succeeds with the
 *                                      code_verifier THIS browser stored when
 *                                      it started the flow (binding)
 *   - `?token_hash=&type=`           → verified OFF the main client, then
 *   - `#access_token=…` (legacy)       the user must explicitly confirm the
 *                                      identity ("Continue as Y?", and "you
 *                                      are signed in as X" if applicable)
 *                                      before anything is stored
 *   - /auth/kiosk-relay fragment     → kept in memory for the relay only,
 *                                      never stored on the relay origin
 */
import type { EmailOtpType } from '@supabase/supabase-js'

export type UrlAuthPayload =
  | { kind: 'none' }
  | { kind: 'error'; message: string }
  | { kind: 'pkce'; code: string }
  | { kind: 'token_hash'; tokenHash: string; type: EmailOtpType }
  | { kind: 'implicit'; accessToken: string; refreshToken: string; type: string | null }

export interface CapturedAuthUrl {
  pathname: string
  payload: UrlAuthPayload
  /** Same-origin path from `?next=` (already open-redirect safe), or null. */
  next: string | null
  /** Link type hint (`recovery`, `invite`, `magiclink`, …) or null. */
  type: string | null
}

const EMAIL_OTP_TYPES: readonly EmailOtpType[] = ['signup', 'invite', 'magiclink', 'recovery', 'email_change', 'email']

/** Routes on which a URL-delivered session may be considered at all.
 *  `/`, `/login` and `/reset-password` are where production email links land
 *  today (admin magic link, platform-add-agency invite, recovery/invite). */
export const AUTH_CALLBACK_PATHS: readonly string[] = [
  '/auth/callback', '/reset-password', '/login', '/', '/auth/kiosk-relay',
  // Legacy: inspector-mobile reset emails sent before S3b pointed here (no
  // such route ever existed); recovery links are routed to /reset-password.
  '/auth/update-password',
]

export const KIOSK_RELAY_PATH = '/auth/kiosk-relay'

/** Credential-bearing params removed from the URL on every route. */
const CREDENTIAL_PARAMS = ['access_token', 'refresh_token', 'provider_token', 'provider_refresh_token', 'expires_in', 'expires_at', 'token_type', 'token_hash']
/** Removed only on callback paths (`code` is a common name elsewhere). */
const CALLBACK_ONLY_PARAMS = ['code', 'error', 'error_code', 'error_description', 'sb']

export function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '') || '/'
  return pathname || '/'
}

export function isAuthCallbackPath(pathname: string): boolean {
  return AUTH_CALLBACK_PATHS.includes(normalizePath(pathname))
}

/** Same-origin relative path or '/'. Mirrors kioskRelaySecurity.safeNextPath. */
export function safeNextPath(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2048) return '/'
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/'
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return '/'
  try {
    const base = 'https://next.invalid'
    if (new URL(raw, base).origin !== base) return '/'
  } catch {
    return '/'
  }
  return raw
}

function hashParams(hash: string): URLSearchParams {
  return new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
}

/** Pure: classify the auth parameters in a location. */
export function parseAuthUrl(search: string, hash: string): UrlAuthPayload {
  const q = new URLSearchParams(search)
  const h = hashParams(hash)
  const get = (k: string) => h.get(k) ?? q.get(k)

  const errorDescription = get('error_description')
  if (get('error') || errorDescription || get('error_code')) {
    return { kind: 'error', message: (errorDescription ?? get('error') ?? 'Link error').slice(0, 300) }
  }

  const accessToken = h.get('access_token') ?? q.get('access_token')
  const refreshToken = h.get('refresh_token') ?? q.get('refresh_token')
  if (accessToken || refreshToken) {
    if (!accessToken || !refreshToken) return { kind: 'error', message: 'Incomplete sign-in link' }
    return { kind: 'implicit', accessToken, refreshToken, type: get('type') }
  }

  const tokenHash = q.get('token_hash') ?? h.get('token_hash')
  if (tokenHash) {
    const t = get('type')
    if (!t || !(EMAIL_OTP_TYPES as readonly string[]).includes(t)) return { kind: 'error', message: 'Invalid sign-in link' }
    return { kind: 'token_hash', tokenHash, type: t as EmailOtpType }
  }

  const code = q.get('code')
  if (code) return { kind: 'pkce', code }

  return { kind: 'none' }
}

/** Pure: the same URL with credential params removed (kept: path, `type`,
 *  `email`, `next` and anything unrelated). Returns null when nothing changes. */
export function scrubAuthUrl(href: string): string | null {
  const url = new URL(href)
  const callback = isAuthCallbackPath(url.pathname)
  const remove = callback ? [...CREDENTIAL_PARAMS, ...CALLBACK_ONLY_PARAMS] : CREDENTIAL_PARAMS
  let changed = false

  for (const k of remove) {
    if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true }
  }
  if (url.hash) {
    const h = hashParams(url.hash)
    // Only touch fragments that look like auth fragments; others (e.g. the
    // kiosk arrive `#at=&rt=&n=` shape) are left for their own page.
    if (remove.some(k => h.has(k))) {
      for (const k of remove) h.delete(k)
      const rest = h.toString()
      url.hash = rest ? `#${rest}` : ''
      changed = true
    }
  }
  return changed ? url.toString() : null
}

/** Pure: capture + classification for a location. */
export function readAuthUrl(href: string): CapturedAuthUrl {
  const url = new URL(href)
  const payload = parseAuthUrl(url.search, url.hash)
  const h = hashParams(url.hash)
  const rawNext = url.searchParams.get('next')
  return {
    pathname: normalizePath(url.pathname),
    payload,
    next: rawNext ? safeNextPath(rawNext) : null,
    type: h.get('type') ?? url.searchParams.get('type'),
  }
}

let captured: CapturedAuthUrl | null = null

/**
 * Called once at startup (src/lib/supabase.ts), BEFORE anything else can
 * read or rewrite the URL: remembers the auth parameters in memory and
 * scrubs them from the address bar and history entry.
 */
export function captureAuthUrlOnce(): CapturedAuthUrl {
  if (captured) return captured
  if (typeof window === 'undefined') {
    captured = { pathname: '/', payload: { kind: 'none' }, next: null, type: null }
    return captured
  }
  captured = readAuthUrl(window.location.href)
  const scrubbed = scrubAuthUrl(window.location.href)
  if (scrubbed) {
    try { window.history.replaceState(window.history.state, '', scrubbed) } catch { /* ignore */ }
  }
  return captured
}

/** The captured payload (does not consume it). */
export function getCapturedAuthUrl(): CapturedAuthUrl {
  return captured ?? captureAuthUrlOnce()
}

/** For the kiosk relay only: the URL-delivered tokens, never persisted here. */
export function getRelayHandoffTokens(): { accessToken: string; refreshToken: string } | null {
  const c = getCapturedAuthUrl()
  if (c.pathname !== KIOSK_RELAY_PATH || c.payload.kind !== 'implicit') return null
  return { accessToken: c.payload.accessToken, refreshToken: c.payload.refreshToken }
}

let processed = false
/** True once AuthUrlGate has taken the captured payload (single use). */
export function isAuthUrlProcessed(): boolean { return processed }
/** Claims the captured payload for processing; false if already claimed. */
export function claimAuthUrlProcessing(): boolean {
  if (processed) return false
  processed = true
  return true
}

/** Test helper. */
export function __resetCapturedAuthUrlForTests(): void { captured = null; processed = false }

/** Where to go after a URL-delivered session was accepted. Recovery and
 *  invite links always go to the set-password page (keeping its `type`);
 *  everything else goes to the safe `next` or stays on the current path. */
export function postAcceptPath(c: CapturedAuthUrl, currentPath: string, currentSearch: string): string | null {
  const type = c.type ?? (c.payload.kind === 'token_hash' ? c.payload.type : null)
  if (type === 'recovery' || type === 'invite') {
    if (normalizePath(currentPath) === '/reset-password') return null
    const q = new URLSearchParams(currentSearch)
    const out = new URLSearchParams()
    const email = q.get('email')
    if (email) out.set('email', email)
    if (type === 'invite') out.set('type', 'invite')
    const s = out.toString()
    return `/reset-password${s ? `?${s}` : ''}`
  }
  if (c.next) return c.next
  if (normalizePath(currentPath) === '/auth/callback') return '/'
  return null
}
